import React from 'react';
import { styled } from '@mui/material';

import { NoTemplates } from 'components/organisms/TaskTemplates';
import {
    Button,
    Card,
    Checkbox,
    Divider,
    Dropdown,
    Icon,
    IconButton,
    SplitButton,
    Stack,
    TextInput,
    Text,
} from 'shell/components';
import { getDueDate } from 'utils/helper';

const Mobile = ({
    filterState,
    numFilters,
    setFilterState,
    taskTemplates,
    checked,
    onChangeCheck,
    getTemplateStatus,
    getCompletion,
    onClickTemplate,
    onClickCreate,
    onClickPublishMulti,
    onClickDeleteMulti,
    duplicateTemplate,
    deleteTemplateWithConfirmation,
}) => {
    const handleClickSplitButton = ({ key }) => {
        if (key === 'publish') onClickPublishMulti();
        if (key === 'delete') onClickDeleteMulti();
    };

    const handleSelect = (key, templateId) => {
        switch (key) {
            case 'edit':
                return setTimeout(() => onClickTemplate(templateId));
            case 'duplicate':
                return duplicateTemplate(templateId);
            case 'delete':
                return deleteTemplateWithConfirmation(templateId);
        }
    };

    return (
        <CustomStack numTemplates={taskTemplates.length} spacing={4}>
            <Stack direction={'row'} justifyContent={'space-between'} spacing={2}>
                <Button
                    color={'neutral'}
                    startIcon={<Icon kind={'filter'} />}
                    variant={'tertiary'}
                    onClick={() => setFilterState((current) => ({ ...current, isOpen: true }))}
                >
                    {`Filters ${numFilters ? `(${numFilters})` : ''}`}
                </Button>
                <CustomTextInput
                    disablePointerEventsOnIcon={!Boolean(filterState.searchInput)}
                    endIcon={
                        filterState.searchInput ? (
                            <IconButton
                                aria-label={'clear search input'}
                                kind={'exit'}
                                variant={'square'}
                                onClick={() => setFilterState((current) => ({ ...current, searchInput: '' }))}
                                onMouseDown={(e) => e.preventDefault()}
                            />
                        ) : (
                            <Icon kind={'search'} />
                        )
                    }
                    placeholder={'Search'}
                    value={filterState.searchInput}
                    onChange={(e) => setFilterState((current) => ({ ...current, searchInput: e.target.value }))}
                />
            </Stack>
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Text variant={'p1'}>Name</Text>
                {checked.length ? (
                    <SplitButton
                        options={[
                            { key: 'publish', value: 'Publish' },
                            { key: 'delete', value: 'Delete' },
                        ]}
                        onClick={handleClickSplitButton}
                    />
                ) : (
                    <IconButton
                        data-cy={'task-template-add-task-button'}
                        aria-label={'create new task template'}
                        kind={'add'}
                        size={'small'}
                        variant={'circle'}
                        onClick={onClickCreate}
                    />
                )}
            </Stack>
            {taskTemplates.length ? (
                taskTemplates.map((template) => (
                    <CustomCard key={template.id} onClick={() => onClickTemplate(template.id)}>
                        <Stack divider={<Divider />} spacing={2}>
                            <Stack
                                alignItems={'flex-start'}
                                direction={'row'}
                                justifyContent={'space-between'}
                                spacing={2}
                            >
                                <Checkbox
                                    sx={{ m: -2 }}
                                    checked={checked.includes(template.id)}
                                    onChange={() => onChangeCheck(template.id)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <Text
                                    data-cy={template.title}
                                    sx={{ flex: 1 }}
                                    align={'left'}
                                    variant={'sh2'}
                                    weight={'semibold'}
                                >
                                    {template.title}
                                </Text>
                                <Dropdown
                                    id={`${template.id}-dropdown`}
                                    sx={{ alignSelf: 'center' }}
                                    options={[
                                        { key: 'edit', value: 'Edit' },
                                        { key: 'duplicate', value: 'Duplicate' },
                                        { key: 'delete', value: 'Delete' },
                                    ]}
                                    rootComponent={
                                        <IconButton
                                            aria-label={'more options'}
                                            kind={'moreHorizontal'}
                                            size={'medium'}
                                            variant={'square'}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    }
                                    onSelect={({ key }) => handleSelect(key, template.id)}
                                />
                            </Stack>
                            <Stack spacing={2}>
                                <Text color={'light'} variant={'p2'}>
                                    {getTemplateStatus(template)}
                                </Text>
                                <Text color={'light'} variant={'p2'}>
                                    Due: {getDueDate(template)}
                                </Text>
                                <Text color={'light'} variant={'p2'}>
                                    Completion: {getCompletion(template)}
                                </Text>
                            </Stack>
                        </Stack>
                    </CustomCard>
                ))
            ) : (
                <NoTemplates />
            )}
        </CustomStack>
    );
};

const CustomStack = styled(Stack)(({ numTemplates, theme }) => {
    return {
        padding: theme.spacing(4, 0, 8),
        ...(!numTemplates && {
            paddingBottom: 0,
        }),
    };
});

const CustomTextInput = styled(TextInput)(() => {
    return {
        flex: 1,
    };
});

const CustomCard = styled(Card)(({ theme }) => {
    return {
        '& > .MuiCardActionArea-root': {
            padding: theme.spacing(3),
        },
    };
});

export default Mobile;
